import { localizedStrings } from '@core/strings'
import React from 'react'
import { Box, Text, Flex } from '@fundamentals'
import { Card, Chip } from '@common/material'
import EventBusyOutlined from '@mui/icons-material/EventBusyOutlined'
import EventOutlined from '@mui/icons-material/EventOutlined'
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LockOutlined from '@mui/icons-material/LockOutlined'
import moment from 'moment/moment'
import { useModal } from '@common/GlobalModal'
import ChecklistDetailsModal from '@modules/permitDetails/modals/ChecklistDetailsModal'
import { PermitTask } from '@core/entities/task/PermitTask'
import { Palette } from '@core/theme/color-palette-siteassist'

type PermitTasksTabProps = {
  tasks: PermitTask[]
  organisationId: number
  projectId?: number
  permitStartsOn?: string
}

export const PermitTasksTab: React.FC<PermitTasksTabProps> = ({
  tasks,
  projectId,
  organisationId,
  permitStartsOn,
}) => {
  const { showModal } = useModal()

  return (
    <Box>
      {tasks?.map((task) => {
        const assignee = task.status === 'DONE' ? task.doneBy : task.assignedTo
        const isLockedBySchedule = task.isLockedBySchedule()
        const dateLabel =
          task.status === 'DONE'
            ? localizedStrings.completedDate
            : localizedStrings.dueDate
        const assigneeLabel =
          task.status === 'DONE'
            ? localizedStrings.completedBy
            : localizedStrings.assignTo
        const dateValue =
          task.status === 'DONE' ? task?.modifiedOn : permitStartsOn

        const isChecklistTask = task.type === 'PERMIT_PRE_START_CHECKLIST'
        const isClickable = isChecklistTask && task.status === 'DONE'

        return (
          <Card
            key={task.id}
            sx={{
              px: 3,
              py: 2,
              mb: 2,
              cursor: isClickable ? 'pointer' : 'default',
            }}
            onClick={() => {
              if (isClickable) {
                showModal(
                  <ChecklistDetailsModal
                    checklistId={task.checklistId}
                    organisationId={organisationId}
                    projectId={projectId}
                  />,
                )
              }
            }}
          >
            <Text fontWeight={500} fontSize='1rem' sx={{ mb: 1 }}>
              {task.name}
            </Text>

            <Chip
              size='small'
              label={task.getStatusText(task.status)}
              color={task.getStatusColor()}
              sx={{ mb: 1.5, height: 22 }}
            />

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'max-content minmax(0, 1fr)',
                columnGap: 3,
                rowGap: 0.5,
                alignItems: 'center',
              }}
            >
              {isLockedBySchedule && task.earliestCompletionAt && (
                <>
                  <Flex alignItems='center'>
                    <EventAvailableOutlined
                      sx={{ fontSize: 16, color: 'action.active' }}
                    />
                    <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                      {localizedStrings.startDate}
                    </Text>
                  </Flex>
                  <Text sx={{ fontSize: 14 }}>
                    {moment(task.earliestCompletionAt).format(
                      'DD MMM YYYY [at] HH:mm',
                    )}
                  </Text>
                </>
              )}
              <>
                <Flex alignItems='center'>
                  {task.status === 'DONE' ? (
                    <EventOutlined
                      sx={{ fontSize: 16, color: 'action.active' }}
                    />
                  ) : (
                    <EventBusyOutlined
                      sx={{ fontSize: 16, color: 'action.active' }}
                    />
                  )}
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {dateLabel}
                  </Text>
                </Flex>
                <Text sx={{ fontSize: 14 }}>
                  {moment(dateValue).format('DD MMM YYYY [at] HH:mm')}
                </Text>
              </>
              <>
                <Flex alignItems='center'>
                  <PersonOutlinedIcon
                    sx={{ fontSize: 16, color: 'action.active' }}
                  />
                  <Text color='text.secondary' sx={{ fontSize: 14, ml: 0.5 }}>
                    {assigneeLabel}
                  </Text>
                </Flex>
                <Text sx={{ fontSize: 14 }}>
                  {`${assignee?.firstName ?? ''} ${
                    assignee?.lastName ?? ''
                  }`.trim()}
                </Text>
              </>
            </Box>

            {isLockedBySchedule && task.earliestCompletionAt && permitStartsOn && (
              <Flex
                alignItems='center'
                sx={{ mt: 1, p: 2 }}
                gap={0.5}
                bgcolor={Palette.Amber[50]}
                borderRadius={1}
              >
                <LockOutlined sx={{ fontSize: 20, color: 'action.active' }} />
                <Text sx={{ fontSize: 12, fontWeight: 500 }}>
                  {localizedStrings.taskLockedUntil(
                    moment(task.earliestCompletionAt).format(
                      'DD MMM YYYY [at] HH:mm',
                    ),
                    task.getScheduleDurationLabel(permitStartsOn),
                  )}
                </Text>
              </Flex>
            )}

            {task.status === 'TO_DO' && !isLockedBySchedule && (
              <Flex
                alignItems='center'
                sx={{ mt: 1, p: 2 }}
                gap={0.5}
                bgcolor={Palette.Violet[50]}
                borderRadius={1}
              >
                <InfoOutlinedIcon sx={{ fontSize: 20 }} color='primary' />
                <Text sx={{ fontSize: 12, fontWeight: 500 }}>
                  {localizedStrings.taskMustBeCompletedOnMobile}
                </Text>
              </Flex>
            )}
          </Card>
        )
      })}
      {(!tasks || tasks.length === 0) && (
        <Card sx={{ px: 3, py: 2, mb: 2 }}>
          <Text>{localizedStrings.noTasksFound}</Text>
        </Card>
      )}
    </Box>
  )
}
